import { useEffect, useState } from "react";
import { ApiError, api, type NotaOut } from "@/lib/api";
import { useToast } from "@/ui/Toast";

/**
 * Lista compacta de notas MANUALES del caso (las que escribio el equipo,
 * no solo las tuyas), en modo lectura.
 *
 * Filtra fuera las notas SISTEMA (cambios de hitos, cambios de estado IMEI,
 * creacion, cierre, etc.) porque son ruido en el contexto del mensaje al
 * cliente — el usuario quiere ver lo que ANOTO, no el audit log automatico.
 * Para ver el log completo va al tab "Bitacora interna".
 *
 * Refetchea cuando cambia `casoUpdatedAt` (caso editado por algun lado).
 */
export default function BitacoraReferencia({
  casoId,
  casoUpdatedAt,
}: {
  casoId: number;
  casoUpdatedAt: string;
}) {
  const toast = useToast();
  const [notas, setNotas] = useState<NotaOut[]>([]);
  const [loading, setLoading] = useState(true);
  // Plegada por default: es contexto de consulta, no algo que se edite
  // aquí, y abierta empujaba el editor fuera de la pantalla.
  const [colapsado, setColapsado] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<NotaOut[]>(`/api/casos/${casoId}/notas`)
      .then((list) => {
        if (!cancelled) {
          // Solo notas manuales — las SISTEMA (cambios de estado, hitos)
          // son ruido aqui. Para verlas, el tab "Bitacora interna".
          setNotas(list.filter((n) => n.tipo === "manual"));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude cargar la bitácora", msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casoId, casoUpdatedAt]);

  return (
    // Contenedor delgado (no `card`): su padding pesaba más que el contenido
    // y competía visualmente con el editor, que es lo importante del modal.
    <div className="rounded border border-ink-700 bg-ink-950/40 px-2 py-1.5 space-y-2">
      <div className="flex items-center justify-between">
        {/* «solo notas del equipo»: antes decía «solo tus notas», pero el filtro
            es por tipo (manual), no por autor — salen las de todo el equipo. */}
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-zinc-400">
            Bitácora interna
          </span>
          <span className="text-xs text-zinc-500">
            · solo notas del equipo ({notas.length})
          </span>
        </div>
        <button
          type="button"
          onClick={() => setColapsado((v) => !v)}
          className="text-[10px] text-zinc-600 hover:text-zinc-300 px-1"
        >
          {colapsado ? "▶ mostrar" : "▼ ocultar"}
        </button>
      </div>

      {!colapsado && (
        <div className="max-h-[200px] overflow-y-auto pr-1">
          <p className="mb-1 text-[11px] text-zinc-600">
            Sin los cambios automáticos (hitos, estados de IMEI…): esos están completos en la
            pestaña «Bitácora interna».
          </p>
          {loading ? (
            <p className="text-sm text-zinc-500">Cargando bitácora...</p>
          ) : notas.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">
              Sin notas del equipo todavía. Agrega desde la pestaña "Bitácora interna".
            </p>
          ) : (
            <ul className="divide-y divide-ink-700/60">
              {notas.map((n) => (
                <li key={n.id} className="py-1.5 flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
                  <span className="text-[10px] text-zinc-500 whitespace-nowrap flex-shrink-0 pt-0.5">
                    {new Date(n.ts).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">
                      {n.texto}
                    </p>
                    {(n.imei != null || n.tags.length > 0) && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {n.imei != null && (
                          <span className="text-[9px] uppercase tracking-wide px-1 py-0 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300 font-mono">
                            IMEI {n.imei}
                          </span>
                        )}
                        {n.tags.map((t) => (
                          <span
                            key={t}
                            className="text-[9px] uppercase tracking-wide px-1 py-0 rounded border border-accent-500/20 bg-accent-500/5 text-accent-300/80"
                          >
                            #{t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-zinc-500 flex-shrink-0 pt-0.5 sm:max-w-[8rem] truncate">
                    {n.autor_nombre || ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
